import { Link } from "react-router-dom";
import { ABOUT_PAGE } from "@/constants/testIds";
import { Award, Shield, Sparkles, HeartHandshake, Phone } from "lucide-react";
import useSeo from "@/hooks/useSeo";

const PHONE_DISPLAY = "(705) 796-4025";
const PHONE_HREF = "tel:+17057964025";

const VALUES = [
  { icon: HeartHandshake, title: "Local & accountable", body: "Your local patch & repair specialist. I'm right here around the corner, easy to reach, and committed to delivering high-quality workmanship with complete accountability from start to finish.", bg: "var(--coral)", text: "var(--cream)" },
  { icon: Sparkles, title: "Obsessive detail", body: "We don't just patch holes; we sweat the millimeter details. Every repair is carefully checked under our bright hand-held lights, at every angle to ensure the texture and patch blends into your existing ceilings and walls — completely out of sight.", bg: "var(--mustard)", text: "var(--ink)" },
  { icon: Shield, title: "Clean job site", body: "We respect your space. Every drywall repair includes full containment of the work area, dust control, and a thorough final clean up so your home is left spotless and ready to enjoy.", bg: "var(--teal)", text: "var(--cream)" },
  { icon: Award, title: "Customer satisfaction", body: "Great customer satisfaction in drywall repairs means leaving the homeowner with an invisible patch, and a stress-free experience from start to finish.", bg: "var(--cream-soft)", text: "var(--ink)" },
];

export default function About() {
  useSeo({
    title: "About · Drywall Repair in Wasaga Beach · Southern Georgian Bay Repair Expert",
    description:
      "Locally owned since 1987. Smooth Finish — Drywall Repair in Wasaga Beach and area, your Southern Georgian Bay Repair Expert. Quality workmanship, fully insured. Call (705) 796-4025.",
    path: "/about",
  });
  return (
    <div data-testid={ABOUT_PAGE.root}>
      {/* HERO */}
      <section className="pt-12 pb-24 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[480px] h-[480px] rounded-full bg-[var(--teal)] opacity-15 blur-3xl"/>
        <div className="container-x relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="chip">ABOUT US</span>
            <h1 className="display-xl mt-5">
              Built on<br/>
              <span className="text-[var(--coral)]">smooth walls</span><br/>
              and quality workmanship.
            </h1>
            <p className="mt-7 text-lg text-[var(--ink-soft)] max-w-xl">
              Smooth Finish started in 1987 out of a single pickup truck with one rule:
              <span className="font-semibold text-[var(--ink)]"> mastering the art of the invisible patch.</span>
            </p>
            <p className="mt-4 text-[var(--ink)] font-semibold max-w-xl">
              Three decades and 500+ homes later, that rule hasn't changed. We're a small, locally-owned crew —
              every quote, repair and final sign-off goes through the same hands.
            </p>
            <p className="mt-6 text-[var(--ink-soft)] italic max-w-xl">
              Smooth Finish Drywall Repairs is owned and operated by Mike Miller Drywall—the same trusted local service, now with a new look.
            </p>
            <p className="mt-4 text-[var(--ink)] font-semibold max-w-xl">
              Proudly serving the Southern Georgian Bay region — specializing in local expert repair from Collingwood to Bluewater Beach.
            </p>
            <div className="mt-9 flex gap-3 flex-wrap">
              <a href={PHONE_HREF} className="btn-primary"><Phone size={18}/> Call Mike for a free estimate</a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] max-w-[520px] mx-auto">
              <div className="absolute inset-0 rounded-[40px] bg-[var(--coral)] translate-x-4 translate-y-4"/>
              <div className="absolute inset-0 rounded-[36px] overflow-hidden photo-modern">
                <img src="/about-drywall.jpg" alt="Hands patching a drywall crack with joint compound and mesh tape"/>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[var(--ink)] text-[var(--cream)] rounded-2xl px-5 py-4 max-w-[230px] shadow-xl">
                <div className="font-display text-2xl leading-tight">"Mastering the art of the invisible patch."</div>
                <div className="text-[0.65rem] uppercase tracking-widest text-[#E8E1D2]/60 mt-2">— Our rule, since 1987</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section pt-0">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <span className="num-badge">— OUR VALUES</span>
            <h2 className="display-xl mt-4">Four things we<br/>won't compromise on.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={v.title} className="rounded-3xl p-8 min-h-[300px] flex flex-col" style={{ background: v.bg, color: v.text }}>
                <div className="w-14 h-14 rounded-full bg-black/10 flex items-center justify-center mb-6">
                  <v.icon size={22}/>
                </div>
                <h3 className="display-lg" style={{ color: v.text }}>{v.title}</h3>
                <p className="mt-3 text-sm opacity-85 flex-1">{v.body}</p>
                <div className="font-display font-bold text-3xl opacity-50 mt-6">0{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PROMISE */}
      <section className="section pt-0">
        <div className="container-x">
          <div className="rounded-[40px] bg-[var(--coral)] text-[var(--cream)] p-12 sm:p-20 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--mustard)] opacity-80 pointer-events-none"/>
            <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[var(--teal)] opacity-70 pointer-events-none"/>

            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="chip cream-on-dark">The No Mess No Stress Promise</span>
                <h2 className="display-xl mt-5 text-[var(--cream)]">
                  Mastering the art<br/>
                  <span className="text-[var(--ink)]">of the invisible patch.</span>
                </h2>
                <p className="mt-6 text-[var(--cream)] text-lg max-w-xl">
                  The rule hasn't changed in 30 plus years; true quality lies in the details, and a great patch should look like it was never there at all.
                </p>
                <a href={PHONE_HREF} className="btn-cream mt-8 !text-base"><Phone size={18}/> Call Mike for a free estimate</a>
              </div>

              <div className="lg:col-span-5 space-y-3">
                {[
                  { t: "Written quote first", b: "Before we lift a trowel — no surprises." },
                  { t: "Dust-controlled work", b: "Plastic up, floors covered, vents sealed." },
                  { t: "Same hands, every step", b: "Quote to final sign-off — one crew." },
                ].map((it, i) => (
                  <div key={it.t} className="bg-[var(--ink)] rounded-2xl p-6 flex items-start gap-4">
                    <div className="font-display font-bold text-3xl text-[var(--coral)] shrink-0 leading-none">0{i + 1}</div>
                    <div>
                      <div className="font-display font-bold text-xl text-[var(--cream)] leading-tight">{it.t}</div>
                      <div className="text-sm text-[#E8E1D2]/70 mt-1">{it.b}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
